import { DentalCase } from "@/typed/DentalCase";
import { allowedModelFileExts, ModelFileTypes } from "@/typed/ModelFileTypes";
import { ModelLabel } from "@/typed/ModelTypes";

class NoScansDetectedError extends Error {}
class EmptyError extends Error {}

const COPY_INDICATOR: RegExp = /^(.*?) \((copy)?( )?\d*\)$|^(.*?) \d*$|^(.*?)$/;

enum LabelIndicatorType {
  SUFFIX,
  PREFIX,
  EQUALS
}
interface LabelIndicator {
  regex: RegExp;
  type: LabelIndicatorType;
}

const LOWER_INDICATORS: LabelIndicator[] = [
  { regex: /^(.*?)[-_ ]l$/, type: LabelIndicatorType.SUFFIX },
  { regex: /^(.*?)[-_]?lowerjawscan$/, type: LabelIndicatorType.SUFFIX },
  { regex: /^(.*?)[-_]?lowerjaw$/, type: LabelIndicatorType.SUFFIX },
  { regex: /^(.*?)[-_]?lower_arch$/, type: LabelIndicatorType.SUFFIX },
  { regex: /^mandibular[-_]?(.*)$/, type: LabelIndicatorType.PREFIX },
  { regex: /^(.+?)[-_]?lower$/, type: LabelIndicatorType.SUFFIX },
  { regex: /^lower[-_]?(.*)$/, type: LabelIndicatorType.PREFIX }
];

const UPPER_INDICATORS: LabelIndicator[] = [
  { regex: /^(.*?)[-_ ]u$/, type: LabelIndicatorType.SUFFIX },
  { regex: /^(.*?)[-_]?upperjawscan$/, type: LabelIndicatorType.SUFFIX },
  { regex: /^(.*?)[-_]?upperjaw$/, type: LabelIndicatorType.SUFFIX },
  { regex: /^(.*?)[-_]?upper_arch$/, type: LabelIndicatorType.SUFFIX },
  { regex: /^maxillary[-_]?(.*)$/, type: LabelIndicatorType.PREFIX },
  { regex: /^(.+?)[-_]?upper$/, type: LabelIndicatorType.SUFFIX },
  { regex: /^upper[-_]?(.*)$/, type: LabelIndicatorType.PREFIX }
];

const fallbackLabelDetection = (name: string): "upper" | "lower" | undefined => {
  const containsLower = name.toLowerCase().includes("lower") || name.toLowerCase().includes("mandibular");
  const containsUpper = name.toLowerCase().includes("upper") || name.toLowerCase().includes("maxillary");

  if (containsLower && !containsUpper) {
    return "lower";
  }
  if (containsUpper && !containsLower) {
    return "upper";
  }

  // Return undefined when a case may be both or none.
  // The user can manually label the case.
  return undefined;
};

interface ScanNameInformation {
  label: "lower" | "upper" | undefined;
  scanName: string | undefined;
}

/**
 * Extracts the scan label and potential case name from a scan name.
 *
 * Labels are determined by inspecting prefixes and suffixes defined in UPPER_INDICATORS and LOWER_INDICATORS.
 * Copy suffixes (e.g. "file (Copy 2).ply") are stripped before accessing label suffixes.
 *
 * The portion of the filename left after removing the label indicator is returned as a potential case name.
 *
 *  @param {string} name
 * @returns {ScanNameInformation}
 */
const extractScanNameInformation = (name: string): ScanNameInformation => {
  const lowerCaseName = name.toLowerCase();

  // Remove copy prefixes
  const matchCopy = lowerCaseName.match(COPY_INDICATOR);
  let lowerCaseCopySuffixStriped = lowerCaseName;
  if (matchCopy != null && matchCopy.length > 1) {
    lowerCaseCopySuffixStriped = matchCopy.slice(1).find((value) => value != undefined) ?? lowerCaseName;
  }

  // Extract information
  const information: ScanNameInformation[] = [];
  for (const indicator of LOWER_INDICATORS) {
    const name = indicator.type === LabelIndicatorType.SUFFIX ? lowerCaseCopySuffixStriped : lowerCaseName;
    const match = name.match(indicator.regex);
    if (match != null) {
      information.push({
        label: "lower",
        scanName: match.length > 1 && match[1] !== "" ? match[1].trim() : undefined
      });
    }
  }

  for (const indicator of UPPER_INDICATORS) {
    const name = indicator.type === LabelIndicatorType.SUFFIX ? lowerCaseCopySuffixStriped : lowerCaseName;
    const match = name.match(indicator.regex);
    if (match != null) {
      information.push({
        label: "upper",
        scanName: match.length > 1 && match[1] !== "" ? match[1].trim() : undefined
      });
    }
  }

  return information.length === 1
    ? information[0]
    : {
        label: fallbackLabelDetection(name),
        scanName: undefined
      };
};

const getFileName = (file: File) =>
  file.webkitRelativePath != null && file.webkitRelativePath !== "" ? file.webkitRelativePath : file.name;

const getFileFromEntry = (fileEntry: FileSystemFileEntry): Promise<File> =>
  new Promise((resolve) => fileEntry.file((file) => resolve(file)));

const readDirEntries = (dir: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> =>
  new Promise((resolve) => {
    const reader = dir.createReader();
    const entries: FileSystemEntry[] = [];
    const readEntries = () => {
      reader.readEntries((result) => {
        if (!result.length) {
          resolve(entries);
        } else {
          entries.push(...result);
          readEntries();
        }
      });
    };
    readEntries();
  });

const getDataTransferAsEntry = (item: DataTransferItem): FileSystemEntry | undefined | null =>
  item.webkitGetAsEntry ? item.webkitGetAsEntry() : (item as any).getAsEntry?.();

const convertDataTransferToInputFiles = async (items: DataTransferItem[]): Promise<File[]> => {
  const files: File[] = [];

  const addFilesFromEntry = async (entry: FileSystemEntry, parent?: FileSystemDirectoryEntry) => {
    if (entry.isFile) {
      let file = await getFileFromEntry(entry as FileSystemFileEntry);
      if (parent != undefined && !file.name.startsWith(parent.fullPath)) {
        file = new File([await file.arrayBuffer()], `${parent.fullPath}/${file.name}`, {
          lastModified: file.lastModified
        });
      }
      files.push(file);
    } else if (entry.isDirectory) {
      const dirEntry = entry as FileSystemDirectoryEntry;
      const entries = await readDirEntries(dirEntry);

      for (const entry of entries) {
        await addFilesFromEntry(entry, dirEntry);
      }
    }
  };

  const processTasks = items.map(async (item: DataTransferItem) => {
    const entry = getDataTransferAsEntry(item);
    if (!entry) {
      return;
    }

    await addFilesFromEntry(entry);
  });

  await Promise.allSettled(processTasks);
  return files;
};

/**
 * This function converts a list of data transfer items into dental cases.
 * See processCaseInputFiles for more information.
 *
 * @param {DataTransferItem[]} dataTransfer
 * @param {string} defaultCaseName
 * @returns {Map<string, DentalCase>} A map of case ids to dental cases.
 */
const processCaseInputDataTransfer = async (dataTransfer: DataTransferItem[], defaultCaseName: string) => {
  const files = await convertDataTransferToInputFiles(dataTransfer);

  return await processCaseInputFiles(files, defaultCaseName);
};

/**
 * This functions converts a list of files into dental cases.
 * It is assumed that each file has it's full path either in the webkitRelativePath or name field.
 *
 * If only files are uploaded then the filename is used to place each scan into a dental case.
 * If folders are uploaded then each folder is turned into a dental case. Only "leaf" folders will be considered.
 *
 * The label of each scan is determined using extractScanNameInformation.
 *
 * If no files are given an EmptyError is thrown. If no scans are detected a NoScansDetectedError is thrown.
 *
 * @param {File[]} files
 * @param {string} defaultCaseName
 * @returns {Map<string, DentalCase>} A map of case ids to dental cases.
 */
const processCaseInputFiles = async (files: File[], defaultCaseName: string) => {
  const casesMap = new Map<string, DentalCase>();
  const hasSubDirectories = new Map<string, boolean>();

  if (files.length === 0) {
    throw new EmptyError();
  }

  // Find leaf directories
  for (const file of files) {
    const name = getFileName(file);

    const directories = name.split("/");
    if (directories.length === 1) {
      continue;
    }
    if (directories.length === 2) {
      hasSubDirectories.set("/", true);
    }

    hasSubDirectories.set(directories.slice(0, -2).join("/"), true);
  }

  // Process each file and ignore non-leaf directories
  for (const file of files) {
    const name = getFileName(file);
    const directories = name.includes("/") ? name.split("/") : [name];

    // Determine parent directory and skip non-leaf directories
    let parentDir = "/";
    if (directories.length === 1) {
      if (hasSubDirectories.get(parentDir)) {
        continue;
      }
    } else {
      parentDir = directories.slice(0, -1).join("/");
      if (hasSubDirectories.get(parentDir)) {
        continue;
      }
    }

    // Ignore invalid file extensions
    const filename = directories.at(-1);
    if (filename === undefined) {
      continue;
    }

    const lastDotIndex = filename.lastIndexOf(".");
    if (lastDotIndex === -1) {
      continue;
    }

    const filenameWithoutExtension = filename.substring(0, lastDotIndex);
    const extension = filename.substring(lastDotIndex + 1).toLowerCase();

    if (!allowedModelFileExts.includes(extension as ModelFileTypes)) {
      continue;
    }

    // Determine case name
    const { label, scanName } = extractScanNameInformation(filenameWithoutExtension);
    let caseName = scanName ?? defaultCaseName;
    const folderName = directories.slice(-2, -1)[0];
    if (folderName != undefined) {
      caseName = folderName;
    }

    // Determine case id
    let caseID;
    if (parentDir === "/") {
      // The upload structure is flat. All files are in the root directory and must be separated by inferred name.
      caseID = caseName;
    } else {
      // The upload structure is nested, Treat each directory as a unique case.
      caseID = parentDir;
    }

    // Add the file to the appropriate case
    if (!casesMap.has(caseID)) {
      casesMap.set(caseID, { name: caseName, scans: [] });
    }

    const caseScans = casesMap.get(caseID)?.scans ?? [];
    caseScans.push({
      scan: file,
      label: label ? (label as ModelLabel) : undefined,
      type: extension as ModelFileTypes,
      lastModified: file.lastModified,
      filename: filenameWithoutExtension
    });
  }

  if (casesMap.size === 0) {
    throw new NoScansDetectedError();
  }

  return casesMap;
};

export { EmptyError, NoScansDetectedError, processCaseInputDataTransfer, processCaseInputFiles };
